import { observer } from 'mobx-react-lite'
import React from 'react'
import { Button, Dropdown, Tooltip } from 'antd'
import type { MenuProps } from 'antd'
import {
    DesktopOutlined,
    ExclamationCircleFilled,
    LogoutOutlined,
    MoonOutlined,
    SunOutlined,
} from '@ant-design/icons'
import i from '../../../translations/i'
import { config } from '../../../config/app'
import { storeLocal } from '../../../helpers/stores'
import { getNameLanguageFromCode, setLanguage } from '../../../helpers/language.helper'
import layoutController, { ThemeMode } from '../../../layouts/controllers/layout.controller'
import './auth_shell.less'

// Каркас страниц без входа в проект: вход, регистрация, восстановление пароля, подтверждение почты, выбор проекта.
// Логотип и карточка с формой по центру страницы.

const themeIcons: Record<ThemeMode, React.ReactNode> = {
    system: <DesktopOutlined />,
    light: <SunOutlined />,
    dark: <MoonOutlined />,
}

const languageItems: MenuProps['items'] = config.languages.map((code, index) => ({
    key: `auth_language_${index}`,
    label: getNameLanguageFromCode(code),
    onClick: () => setLanguage(code),
}))

const Controls = observer(({ logout }: { logout?: boolean }) => (
    <div className="au-controls">
        <Dropdown
            trigger={['click']}
            menu={{
                selectable: true,
                selectedKeys: [layoutController.themeMode],
                items: [
                    { key: 'system', icon: themeIcons.system, label: i.t('def:systemTheme') },
                    { key: 'light', icon: themeIcons.light, label: i.t('def:lightTheme') },
                    { key: 'dark', icon: themeIcons.dark, label: i.t('def:darkTheme') },
                ],
                onClick: ({ key }) => layoutController.setThemeMode(key as ThemeMode),
            }}
        >
            <Button type="text" shape="circle" icon={themeIcons[layoutController.themeMode]} aria-label={i.t('def:systemTheme')} />
        </Dropdown>
        <Dropdown menu={{ items: languageItems }} trigger={['click']}>
            <Button type="text" className="au-controls__lang">{storeLocal.get('lang') || config.defaultLanguage}</Button>
        </Dropdown>
        {logout &&
            <Tooltip placement="bottomRight" title={i.t('def:actions.logout')}>
                <Button type="text" shape="circle" icon={<LogoutOutlined />} onClick={() => layoutController.logout()} />
            </Tooltip>
        }
    </div>
))

// Логотип над карточкой: цветной на светлом фоне, белый — на тёмном
const Logo = observer(() => {
    const wl = layoutController.wl ?? {}
    const logo = layoutController.theme === 'dark'
        ? wl.logo_cloud_white || wl.logo_cloud_color
        : wl.logo_cloud_color || wl.logo_cloud_white
    const name = wl.name || 'SalesKit'

    return (
        <a className="au-logo" href="/">
            {logo ? <img src={logo} alt={name} /> : <span>{name}</span>}
        </a>
    )
})

// Карточка по центру страницы; фон — мягкое свечение в цвете проекта
export const AuthShell = observer(({ children, logout }: { children: React.ReactNode, logout?: boolean }) => (
    <div className="au">
        <div className="au-glow" aria-hidden><i /><i /></div>
        <Controls logout={logout} />

        <Logo />
        <div className="au-card">
            {children}
        </div>

        <div className="au-foot">© {new Date().getFullYear()} {layoutController.wl?.name || 'SalesKit'}</div>
    </div>
))

// ---------- Кусочки форм ----------

export const AuthHead = ({ icon, title, text }: { icon?: React.ReactNode, title: React.ReactNode, text?: React.ReactNode }) => (
    <div className="au-head">
        {icon && <span className="au-head__icon">{icon}</span>}
        <h1 className="au-head__title">{title}</h1>
        {text && <p className="au-head__text">{text}</p>}
    </div>
)

export const AuthError = ({ message }: { message: string }) => message
    ? <div className="au-error" role="alert"><ExclamationCircleFilled />{message}</div>
    : null

export const AuthField = ({ label, extra, children }: { label?: React.ReactNode, extra?: React.ReactNode, children: React.ReactNode }) => (
    <div className="au-field">
        {(label || extra) &&
            <div className="au-field__head">
                {label && <span className="au-field__label">{label}</span>}
                {extra}
            </div>
        }
        {children}
    </div>
)

// Ссылка под формой: «Нет аккаунта? Зарегистрироваться»
export const AuthSwitch = ({ text, link, href }: { text: React.ReactNode, link: React.ReactNode, href: string }) => (
    <div className="au-switch">{text} <a href={href}>{link}</a></div>
)

// Надёжность пароля: длина, буквы в разных регистрах, цифры, спецсимволы
export const passwordScore = (password: string): number => {
    if (!password) return 0
    let score = 0
    if (password.length >= 8) score++
    if (/[a-zа-яё]/.test(password) && /[A-ZА-ЯЁ]/.test(password)) score++
    if (/\d/.test(password)) score++
    if (/[^\wа-яё]/i.test(password)) score++
    return Math.max(1, score)
}

export const PasswordStrength = ({ password }: { password: string }) => {
    if (!password) return null

    const score = passwordScore(password)
    const labels = ['', 'weak', 'fair', 'good', 'strong']

    return (
        <div className={`au-strength au-strength--${score}`}>
            <span className="au-strength__bar"><i /><i /><i /><i /></span>
            <span>{i.t(`authShell:strength.${labels[score]}`)}</span>
        </div>
    )
}
